import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { TourItem, TourSearchContextForSave } from '../../../../../types/tour.types';
import { useSaveTourToIdeaMutation } from '../../../../../store/services/ideasApi';
import { buildTourToIdeaPayload } from '../../../../../utils/tourToIdeaPayload';
import { getApiErrorMessage } from '../../../../../utils/apiErrorMessage';
import { PENDING_TOUR_TO_IDEA_STORAGE_KEY } from '../../../../../constants/discoverIdeaFlow';
import ChooseWhereToSaveModal, {
  type ChooseWhereToSavePayload,
} from '../ChooseWhereToSaveModal/ChooseWhereToSaveModal';

export interface SaveDiscoverModalProps {
  open: boolean;
  onClose: () => void;
  item: TourItem | null;
  searchContext: TourSearchContextForSave | null;
  onSaved?: (message: string) => void;
}

const SaveDiscoverModal: FC<SaveDiscoverModalProps> = ({
  open,
  onClose,
  item,
  searchContext,
  onSaved,
}) => {
  const navigate = useNavigate();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveTourToIdea, { isLoading: isSaving }] = useSaveTourToIdeaMutation();

  useEffect(() => {
    if (!open) {
      setSaveError(null);
    }
  }, [open]);

  const persistPendingIdeaBody = () => {
    if (!item || !searchContext) return false;
    const body = buildTourToIdeaPayload(item, searchContext);
    try {
      sessionStorage.setItem(PENDING_TOUR_TO_IDEA_STORAGE_KEY, JSON.stringify({ body }));
      return true;
    } catch {
      return false;
    }
  };

  const handleCreateNewIdeaList = () => {
    if (!persistPendingIdeaBody()) {
      setSaveError('Could not prepare this tour for a new idea list. Try again.');
      return;
    }
    onClose();
    navigate('/ideas?fromDiscover=1');
  };

  const handleSave = async ({ ideaId }: ChooseWhereToSavePayload) => {
    setSaveError(null);
    if (!item) return;

    if (!searchContext) {
      setSaveError('Search context is missing. Run the search again and try saving.');
      return;
    }

    if (ideaId == null) {
      setSaveError('Select an ideas list to save this tour.');
      return;
    }

    const body = buildTourToIdeaPayload(item, searchContext);
    try {
      const res = await saveTourToIdea({ ideaId, body }).unwrap();
      const msg = res.message || `Saved to ${res.idea_list_name || 'your list'}.`;
      onSaved?.(msg);
      onClose();
    } catch (e: unknown) {
      setSaveError(getApiErrorMessage(e, 'Could not save tour. Try again.', { firstOnly: true }));
    }
  };

  return (
    <ChooseWhereToSaveModal
      open={open}
      onClose={onClose}
      titleId="save-discover-modal-title"
      searchInputName="save-discover-search"
      onSave={handleSave}
      isSaving={isSaving}
      disableFooterWhileSaving
      noIdeaListsEmptyMessage="You have no idea lists yet. Create one below."
      onCreateNewIdeaList={handleCreateNewIdeaList}
      createNewIdeaListDisabled={!item || !searchContext}
      ideasOnly
      errorText={saveError}
      onErrorDismiss={() => setSaveError(null)}
    />
  );
};

export default SaveDiscoverModal;
